import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, Tag, ArrowRight, User } from 'lucide-react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getArticleBySlug, getRelatedArticles } from '@/data/articles';
import { Breadcrumbs } from '@/components/Breadcrumbs';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const relatedArticles = article ? getRelatedArticles(slug!, article.category) : [];

  if (!article) {
    return <Navigate to="/insights" replace />;
  }

  const baseUrl = 'https://smartsite-lead-gen.lovable.app';

  // Enhanced Schema.org Article structured data
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "datePublished": article.date,
    "dateModified": article.date,
    "author": {
      "@type": "Person",
      "name": "James Stanton",
      "url": `${baseUrl}/about`
    },
    "publisher": {
      "@type": "Organization",
      "name": "SmartSite Solutions",
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/logo.png`,
        "width": 512,
        "height": 512
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${baseUrl}/insights/${slug}`
    },
    "articleSection": article.category,
    "wordCount": article.content.join(' ').split(' ').length,
    "image": article.image || `${baseUrl}/og-image.png`
  };

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Insights', href: '/insights' },
    { label: article.title, href: `/insights/${slug}` }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{article.title} | Insights – SmartSite Solutions</title>
        <meta name="description" content={article.excerpt} />
        <link rel="canonical" href={`https://smartsite-lead-gen.lovable.app/insights/${slug}`} />
      </Helmet>
      <Navbar />
      
      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      
      <main className="pt-32 pb-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto">
            {/* Breadcrumbs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Breadcrumbs items={breadcrumbItems} />
              <Link 
                to="/insights"
                className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Insights
              </Link>
            </motion.div>

            {/* Article Header */}
            <motion.header
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-12"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-sm font-medium">
                  {article.category}
                </span>
                {article.featured && (
                  <span className="px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium">
                    Featured
                  </span>
                )}
              </div>
              
              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                {article.title}
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8">
                {article.excerpt}
              </p>
              
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {article.date}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {article.readTime}
                </span>
              </div>
            </motion.header>

            {/* Featured Image Placeholder */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="aspect-video bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl flex items-center justify-center mb-12"
            >
              <span className="font-display text-5xl font-bold text-gradient opacity-30">
                {article.title.split(' ')[0]}
              </span>
            </motion.div>

            {/* Article Content */}
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="prose prose-invert prose-slate max-w-none mb-16"
            >
              {article.content.map((paragraph, index) => (
                <p key={index} className="text-muted-foreground text-lg leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
            </motion.article>

            {/* Author Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 mb-16"
            >
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                  <User className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg mb-1">SmartSite Solutions</h3>
                  <p className="text-sm text-teal-400 mb-3">Digital Marketing for Trades</p>
                  <p className="text-muted-foreground text-sm">
                    We help tradespeople across South Wales get found online. From plumbers to builders, 
                    our team delivers websites that rank and convert.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card p-8 lg:p-12 text-center mb-16"
            >
              <h3 className="font-display text-2xl font-bold mb-4">
                Want Results Like This?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                Book a free 15-minute call. We'll review your current online presence and 
                show you exactly where you're leaving leads on the table.
              </p>
              <Link to="/contact">
                <Button className="gradient-button px-8 py-6 text-lg">
                  Get Your Free Audit
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="font-display text-2xl font-bold mb-8">Related Articles</h2>
              
              <div className="grid md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link 
                    key={related.slug}
                    to={`/insights/${related.slug}`}
                    className="group block"
                  >
                    <div className="glass-card h-full p-6 transition-all duration-500 hover:border-white/20 hover:bg-white/10">
                      <div className="flex items-center gap-2 mb-4">
                        <Tag className="w-3.5 h-3.5 text-teal-400" />
                        <span className="text-xs text-teal-400">{related.category}</span>
                      </div>
                      
                      <h3 className="font-display text-lg font-bold mb-3 group-hover:text-gradient transition-all duration-300 leading-tight">
                        {related.title}
                      </h3>
                      
                      <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                        {related.excerpt}
                      </p>
                      
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{related.date}</span>
                        <span>•</span>
                        <span>{related.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </motion.section>
          )}

          {/* Newsletter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto mt-16"
          >
            <div className="glass-card p-8 lg:p-12 text-center">
              <h3 className="font-display text-2xl font-bold mb-4">
                Get Weekly Insights
              </h3>
              <p className="text-muted-foreground mb-6">
                Join 500+ South Wales trades getting actionable tips to grow their business online.
              </p>
              <form className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 bg-white/5 border-white/10 focus:border-teal-500/50"
                />
                <Button className="gradient-button whitespace-nowrap">
                  Subscribe
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
